<template>
  <div class="padding-10">
    <v-snackbar
      v-model="syncErrorShown"
      color="error"
      top
      right
      multi-line
      :timeout="8000"
    >
      {{ syncError }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="syncErrorShown = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <v-card :elevation="$root.ele" class="mt-5">
      <v-data-table
        :key="tableKey"
        :headers="headers"
        :items="pagedList"
        item-key="groupKey"
        :items-per-page="-1"
        hide-default-footer
        class="templates-table"
      >
        <template v-slot:top>
          <div class="pa-5 d-flex align-center">
            <v-combobox
              label="Search Mechanical Key"
              hide-details
              v-model="searchChips"
              :items="[]"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              multiple
              chips
              append-icon=""
              clearable
              hide-no-data
            >
              <template v-slot:selection="{ item, index }">
                <label class="tag-item">
                  <span class="chip-title">
                    {{ item }}
                  </span>
                  <span
                    class="clear-btn"
                    @click.stop="searchChips.splice(index, 1)"
                  >
                    &times;
                  </span>
                </label>
              </template>
            </v-combobox>
            <v-btn
              class="ml-3"
              color="primary"
              depressed
              tile
              :disabled="!processedList.length"
              @click="exportExcel"
            >
              <v-icon left>
                mdi-microsoft-excel
              </v-icon>
              Export
            </v-btn>
          </div>
        </template>

        <template v-slot:item="{ item }">
          <tr
            v-for="(app, i) in item.applications"
            :key="app.applicationKey"
            :class="{
              'group-first-row': i === 0,
              'application-hover': hoverApplication === app.applicationKey,
            }"
            @mouseenter="hoverApplication = app.applicationKey"
            @mouseleave="hoverApplication = null"
          >
            <td class="application-hover-cell" style="width:10.5%;">{{ app.yearRange }}</td>
            <td class="application-hover-cell" style="width:10.5%;">{{ app.make }}</td>
            <td class="application-hover-cell" style="width:12%;">{{ app.model }}</td>
            <td v-if="i === 0" :rowspan="item.applications.length" style="width:8%; vertical-align:middle;">{{ item.box }}</td>
            <td v-if="i === 0" :rowspan="item.applications.length" style="width:13%; vertical-align:middle;">{{ item.key }}</td>
            <td v-if="i === 0" :rowspan="item.applications.length" style="width:5%; vertical-align:middle; text-align:center;">{{ item.stock === "" ? "-" : item.stock }}</td>
            <td v-if="i === 0" :rowspan="item.applications.length" style="width:7%; vertical-align:middle;">{{ item.mki }}</td>
            <td v-if="i === 0" :rowspan="item.applications.length" style="width:8%; vertical-align:middle;">{{ item.itemNumber }}</td>
            <td v-if="i === 0" :rowspan="item.applications.length" style="width:12%; vertical-align:middle;">{{ item.manufacturer }}</td>
            <td class="application-hover-cell" style="width:7%; text-align:center;">
              <v-btn x-small color="primary" depressed tile @click="openIndividualEdit(app)">
                Edit
              </v-btn>
            </td>
            <td v-if="i === 0" :rowspan="item.applications.length" style="width:7%; vertical-align:middle; text-align:center;">
              <v-btn icon small color="primary" @click="openEditDialog(item)">
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>

        <template v-slot:footer>
          <div class="d-flex align-center justify-end px-4 py-2" style="border-top:thin solid rgba(0,0,0,.12);">
            <span class="body-2 grey--text mr-3">Rows per page:</span>
            <v-select
              v-model="currentItemsPerPage"
              :items="[{text:'10',value:10},{text:'20',value:20},{text:'50',value:50},{text:'All',value:-1}]"
              dense outlined hide-details
              style="max-width:80px;"
              class="body-2 mr-6"
            ></v-select>
            <span class="body-2 grey--text mr-2">{{ pageRangeText }}</span>
            <v-btn icon :disabled="currentPage <= 1" @click="goToPrevPage">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <v-btn icon :disabled="currentPage >= totalPages" @click="goToNextPage">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>
      <v-divider></v-divider>
    </v-card>

    <v-dialog v-model="editDialog" max-width="520px" persistent>
      <v-card>
        <v-card-title>Edit Mechanical Key Group</v-card-title>
        <v-card-text>
          <v-text-field
            label="Box Location"
            outlined
            dense
            persistent-hint
            v-model.trim="editForm.box"
          ></v-text-field>
          <v-text-field
          label="Mechanical Key Name"
          outlined
          dense
          v-model.trim="editForm.key"
          ></v-text-field>
          <v-text-field
            label="Mechanical Key Item#"
            outlined
            dense
            v-model.trim="editForm.mki"
          ></v-text-field>
          <v-text-field
            label="POP-UP Item #"
            outlined
            dense
            v-model.trim="editForm.itemNumber"
          ></v-text-field>
          <v-select
            label="MFG"
            outlined
            dense
            v-model="editForm.manufacturer"
            :items="mfgOptions"
            item-text="value"
            item-value="value"
            clearable
          ></v-select>
          <div class="d-flex align-center">
            <v-btn icon small @click="editForm.stock = Math.max(0, (Number(editForm.stock) || 0) - 1)">
              <v-icon>mdi-minus</v-icon>
            </v-btn>
            <v-text-field
              label="Stock"
              outlined
              dense
              hide-details
              type="number"
              min="0"
              class="mx-2"
              v-model.number="editForm.stock"
            ></v-text-field>
            <v-btn icon small @click="editForm.stock = (Number(editForm.stock) || 0) + 1">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="savingEdit" @click="closeEditDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" depressed :loading="savingEdit" @click="saveEdit">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="individualEditDialog" max-width="520px" persistent>
      <v-card>
        <v-card-title>Edit Individual Application</v-card-title>
        <v-card-text>
          <v-text-field
            label="Make"
            outlined
            dense
            v-model.trim="individualEditForm.makeName"
          ></v-text-field>
          <v-text-field
            label="Model Name"
            outlined
            dense
            v-model.trim="individualEditForm.modelName"
          ></v-text-field>
          <v-text-field
            label="Box Location"
            outlined
            dense
            persistent-hint
            v-model.trim="individualEditForm.box"
          ></v-text-field>
          <v-text-field
            label="Mechanical Key Name"
            outlined
            dense
            v-model.trim="individualEditForm.key"
          ></v-text-field>
          <v-text-field
            label="Mechanical Key Item#"
            outlined
            dense
            v-model.trim="individualEditForm.mki"
          ></v-text-field>
          <v-text-field
            label="POP-UP Item #"
            outlined
            dense
            v-model.trim="individualEditForm.itemNumber"
          ></v-text-field>
          <v-select
            label="MFG"
            outlined
            dense
            v-model="individualEditForm.manufacturer"
            :items="mfgOptions"
            item-text="value"
            item-value="value"
            clearable
          ></v-select>
          <div class="d-flex align-center">
            <v-btn icon small @click="individualEditForm.stock = Math.max(0, (Number(individualEditForm.stock) || 0) - 1)">
              <v-icon>mdi-minus</v-icon>
            </v-btn>
            <v-text-field
              label="Stock"
              outlined
              dense
              hide-details
              type="number"
              min="0"
              class="mx-2"
              v-model.number="individualEditForm.stock"
            ></v-text-field>
            <v-btn icon small @click="individualEditForm.stock = (Number(individualEditForm.stock) || 0) + 1">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="savingIndividualEdit" @click="closeIndividualEditDialog">
            Cancel
          </v-btn>
          <v-btn color="primary" depressed :loading="savingIndividualEdit" @click="saveIndividualEdit">
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { auth, tempModels, functions } from "../assets/js/firebase";
import { errorLogsWrapper } from "../assets/js/errorLogsWrapper";
import { getSyncErrorMessage } from "../assets/js/syncError.helper";
import { exportXlsx } from "../assets/js/xlsx";
import fieldsId from "../assets/constants/additonal.fields.constant";

export default {
  data() {
    return {
      searchChips: [],
      hoverApplication: null,
      pendingModels: [],
      localEdits: [],
      syncError: "",
      syncErrorShown: false,
      unsubscribeTempModels: null,
      editDialog: false,
      editItem: null,
      editForm: {
        box: "",
        key: "",
        mki: "",
        manufacturer: "",
        itemNumber: "",
        stock: "",
      },
      savingEdit: false,
      individualEditDialog: false,
      individualEditItem: null,
      individualEditForm: {
        makeId: "",
        modelId: "",
        makeName: "",
        modelName: "",
        box: "",
        key: "",
        mki: "",
        manufacturer: "",
        itemNumber: "",
        stock: "",
      },
      savingIndividualEdit: false,
      tableKey: 0,
      currentPage: 1,
      currentItemsPerPage: 50,
    };
  },
  computed: {
    mfgOptions() {
      const field = (this.$store.getters.serviceFields || []).find(
        (f) => f.id === fieldsId.MFG
      );
      return field?.options || [];
    },
    defaultMfg() {
      const defaultOption = this.mfgOptions.find(
        (opt) => opt.name?.toLowerCase() === "default"
      );
      if (defaultOption) {
        return defaultOption.value;
      }
      return this.mfgOptions.length > 0 ? this.mfgOptions[0].value : "";
    },
    headers() {
      return [
        { text: "Year Range",      value: "year_range",        width: "10.5%", class: "text-capitalize", align: "start", sortable: false, divider: true },
        { text: "Make",            value: "make",              width: "10.5%", class: "text-capitalize", align: "start", sortable: false, divider: true },
        { text: "Model",           value: "model",             width: "12%", class: "text-capitalize", align: "start", sortable: false, divider: true },
        { text: "Location",        value: "box",               width: "8%", class: "text-capitalize", align: "start", sortable: false, divider: true },
        { text: "MK Name",             value: "key",               width: "13%", class: "text-capitalize", align: "start", sortable: false, divider: true },
        { text: "Stock", value: "stock", width: "5%", class: "text-capitalize", align: "center", sortable: false, divider: true },
        { text: "MK Item#",           value: "mki",               width: "7%", class: "text-capitalize", align: "start", sortable: false, divider: true },
        { text: "POP-UP Item #",          value: "item_number",       width: "8%", class: "text-capitalize", align: "start", sortable: false, divider: true },
        { text: "MFG",    value: "manufacturer",      width: "12%", class: "text-capitalize", align: "start", sortable: false, divider: true },
        { text: "Edit", value: "individual_actions", width: "7%", class: "text-capitalize", align: "center", sortable: false, divider: true },
        { text: "Group Edit",      value: "actions",           width: "7%", class: "text-capitalize", align: "center", sortable: false, divider: true },
      ];
    },
    list() {
      let grouping = {};
      let makes = this.makesWithPendingModels;

      makes.forEach((make) => {
        let models = make.models || [];
        models.forEach((model) => {
          let mechanicalKey = (model.mechanical_key || "").toString().trim();
          if (!mechanicalKey) return;

          let box = "-";
          let mki = "-";
          let key = "-";

          const firstMatch = mechanicalKey.match(/^\((.*)\)/);
          const stockSplit = this.splitStock(firstMatch ? firstMatch[1] : "");
      const locationAndMki = stockSplit.inner;

          const remainingText = mechanicalKey
            .replace(/^\(.*\)\s*/, "")
            .trim();

          if (locationAndMki || stockSplit.stock !== "") {
            if (locationAndMki.toUpperCase().startsWith("MKB#")) {
              // entire content is MKB#... — no box
              mki = locationAndMki.substring(4).trim() || "-";
            } else if (locationAndMki.includes("/")) {
              // split on FIRST slash only to preserve N/A inside mki value
              const slashIndex = locationAndMki.indexOf("/");
              box = locationAndMki.substring(0, slashIndex).trim() || "-";
              const mkiPart = locationAndMki.substring(slashIndex + 1).trim();
              if (mkiPart.toUpperCase().startsWith("MKB#")) {
                mki = mkiPart.substring(4).trim() || "-";
              } else {
                mki = mkiPart || "-";
              }
            } else {
              box = locationAndMki || "-";
            }
            key = remainingText || "-";
          } else {
            key = mechanicalKey.trim() || "-";
          }

          let manufacturer = "-";
          let additionalMfg = (model.mfg || "").toString().trim();
          if (additionalMfg) {
            manufacturer = additionalMfg;
          }

          if (manufacturer === "-") {
            manufacturer = this.defaultMfg || "-";
          }

          let itemNumber = (model.item_number || "").toString().trim() || "-";
          let groupingKey = [box, mki, key, manufacturer, itemNumber].join("|");

          if (grouping[groupingKey] === undefined) {
            grouping[groupingKey] = {
              box,
              mki,
              key,
              manufacturer,
              itemNumber,
              stock: "",
              applications: [],
            };
          }
          if (grouping[groupingKey].stock === "" && stockSplit.stock !== "") {
            grouping[groupingKey].stock = stockSplit.stock;
          }

          let makeName = (make.name || "-").toString().trim() || "-";
          let modelName = (model.name || "-").toString().trim() || "-";
          let yearRange = this.getYearRange(model);
          let applicationKey = `${groupingKey}|${makeName}|${modelName}|${yearRange}`;
          let hasApplication = grouping[groupingKey].applications.some(
            (item) => item.applicationKey === applicationKey
          );

          if (!hasApplication) {
            grouping[groupingKey].applications.push({
              applicationKey,
              make: makeName,
              model: modelName,
              yearRange,
              makeId: make.id,
              modelId: model.uId,
              modelData: model,
            });
          }
        });
      });

      return Object.keys(grouping)
        .map((key) => {
          let item = grouping[key];
          item.applications.sort((a, b) => {
            return this.compareApplications(a, b);
          });
          let makes = item.applications.map((application) => application.make);
          let models = item.applications.map((application) => application.model);
          let yearRanges = item.applications.map(
            (application) => application.yearRange
          );

          return {
            ...item,
            groupKey: key,
            makes,
            models,
            yearRanges,
            make: makes.join("\n"),
            model: models.join("\n"),
            year_range: yearRanges.join("\n"),
          };
        })
    },
    makesWithPendingModels() {
      let makes = (this.$store.getters.makes || []).map((make) => {
        return {
          ...make,
          models: [...(make.models || [])],
        };
      });

      this.pendingModels.forEach((item) => {
        let make = makes.find((make) => make.id === item.makeId);
        if (!make) return;

        if (item.status === "add" && item.data) {
          make.models.unshift(item.data);
          return;
        }

        if (item.status === "update" && item.data) {
          let index = make.models.findIndex((model) => {
            return model.uId === item.modelId || model.uId === item.data.uId;
          });

          if (index >= 0) {
            make.models.splice(index, 1, item.data);
          }
          return;
        }

        if (item.status === "delete") {
          let deleteIds = item.deleteModelId || [];
          make.models = make.models.filter((model) => {
            return !deleteIds.includes(model.uId);
          });
        }
      });

      this.localEdits.forEach((edit) => {
        let make = makes.find((make) => make.id === edit.makeId);
        if (!make) return;
        let index = make.models.findIndex((model) => model.uId === edit.modelId);
        if (index >= 0) {
          make.models.splice(index, 1, { ...make.models[index], ...edit.fields });
        }
      });

      return makes;
    },
    processedList() {
      let searchTerms = this.normalizeSearchChips(this.searchChips).map((term) => {
        return term.toLowerCase();
      });

      let result = this.list.filter((item) => {
        if (!searchTerms.length) return true;
        let searchableText = [
          item.box,
          item.mki,
          item.key,
          item.manufacturer,
          item.itemNumber,
          ...item.applications.reduce((fields, app) => {
            fields.push(app.make, app.model, app.yearRange);
            return fields;
          }, []),
        ]
          .map((field) => (field || "").toString().toLowerCase())
          .join(" ");

        return searchTerms.every((term) => searchableText.includes(term));
      });

      return [...result].sort((a, b) => {
        let makeA = a.applications[0]?.make || "";
        let makeB = b.applications[0]?.make || "";
        let makeSort = makeA.localeCompare(makeB, undefined, { numeric: true, sensitivity: "base" });
        if (makeSort !== 0) return makeSort;

        let modelA = a.applications[0]?.model || "";
        let modelB = b.applications[0]?.model || "";
        return modelA.localeCompare(modelB, undefined, { numeric: true, sensitivity: "base" });
      });
    },
    pagedList() {
      if (this.currentItemsPerPage === -1) return this.processedList;
      const start = (this.currentPage - 1) * this.currentItemsPerPage;
      return this.processedList.slice(start, start + this.currentItemsPerPage);
    },
    totalPages() {
      if (this.currentItemsPerPage === -1) return 1;
      return Math.max(1, Math.ceil(this.processedList.length / this.currentItemsPerPage));
    },
    pageRangeText() {
      const total = this.processedList.length;
      if (!total) return "0–0 of 0";
      if (this.currentItemsPerPage === -1) return `1–${total} of ${total}`;
      const start = (this.currentPage - 1) * this.currentItemsPerPage + 1;
      const end = Math.min(this.currentPage * this.currentItemsPerPage, total);
      return `${start}–${end} of ${total}`;
    },
  },
  methods: {
    normalizeSearchChips(value) {
      if (!Array.isArray(value)) return [];
      return [
        ...new Set(
          value
            .filter((item) => item !== null && item !== undefined)
            .map((item) => item.toString().trim())
            .filter((item) => item)
        ),
      ];
    },
    compareText(a, b) {
      return (a || "").toString().localeCompare((b || "").toString(), undefined, {
        numeric: true,
        sensitivity: "base",
      });
    },
    compareApplications(a, b) {
      if (!a && !b) return 0;
      if (!a) return 1;
      if (!b) return -1;

      let makeSort = this.compareText(a.make, b.make);
      if (makeSort !== 0) return makeSort;

      let modelSort = this.compareText(a.model, b.model);
      if (modelSort !== 0) return modelSort;

      return this.compareText(a.yearRange, b.yearRange);
    },
    exportHeaders() {
      return this.headers.filter((header) => {
        return !["individual_actions", "actions"].includes(header.value);
      });
    },
    getYearRange(model) {
      let from = (model.from || "").toString().trim();
      let to = (model.to || "").toString().trim();

      if (from && to) return `${from} - ${to}`;
      return from || to || "-";
    },
    exportExcel() {
      exportXlsx({
        headers: this.exportHeaders(),
        rows: this.exportRows(),
        sheetName: "Mechanical Key",
        fileName: `mechanical-key-list-${new Date()
          .toISOString()
          .slice(0, 10)}`,
      });
    },
    exportRows() {
      return this.processedList.flatMap((item) => {
        return item.applications.map((application) => {
          return {
            make: application.make,
            model: application.model,
            year_range: application.yearRange,
            item_number: item.itemNumber,
            box: item.box,
            mki: item.mki,
            key: item.key,
            manufacturer: item.manufacturer,
          };
        });
      });
    },
    openEditDialog(item) {
      this.editItem = item;
      this.editForm = {
        box: item.box === "-" ? "" : item.box,
        key: item.key === "-" ? "" : item.key,
        mki: item.mki === "-" ? "" : item.mki,
        manufacturer: item.manufacturer && item.manufacturer !== "-" 
          ? item.manufacturer 
          : this.defaultMfg,
        itemNumber: item.itemNumber === "-" ? "" : item.itemNumber,
        stock: item.stock,
      };
      this.editDialog = true;
    },
    closeEditDialog(force = false) {
      if (this.savingEdit && !force) return;
      this.editDialog = false;
      this.editItem = null;
      this.editForm = {
        box: "",
        key: "",
        mki: "",
        manufacturer: "",
        itemNumber: "",
        stock: "",
      };
    },
    splitStock(inner) {
      const text = (inner || "").toString();
      const match = text.match(/(?:^|\/)\s*STK#\s*(\d*)\s*(?=\/|$)/i);
      if (!match) return { inner: text.trim(), stock: "" };
      const rest = text.replace(match[0], "").replace(/^\//, "").trim();
      return { inner: rest, stock: match[1] === "" ? "" : Number(match[1]) };
    },
    buildKeyText(location, key, mki, stock) {
      let cleanLocation = (location || "")
        .toString()
        .trim()
        .replace(/^\((.*)\)$/, "$1")
        .trim();
      let cleanKey = (key || "").toString().trim();
      let cleanMki = (mki || "").toString().trim();

      let stockText = "";
      if (stock !== "" && stock !== null && stock !== undefined) {
        stockText = `STK#${Math.max(0, Math.floor(Number(stock) || 0))}`;
      }
      let parensContent = [
        cleanLocation,
        cleanMki ? `MKB#${cleanMki}` : "",
        stockText,
      ].filter(Boolean).join("/");

      if (parensContent) return `(${parensContent}) ${cleanKey}`.trim();
      return cleanKey;
    },
    parseKeyText(mechanicalKey) {
      let keyText = (mechanicalKey || "").toString().trim();
      let box = "";
      let mki = "";
      let key = keyText;

      const firstMatch = keyText.match(/^\((.*)\)/);
      const stockSplit = this.splitStock(firstMatch ? firstMatch[1] : "");
      const locationAndMki = stockSplit.inner;
      const remainingText = keyText.replace(/^\(.*\)\s*/, "").trim();
      if (locationAndMki || stockSplit.stock !== "") {
        if (locationAndMki.toUpperCase().startsWith("MKB#")) {
          // entire content is MKB#... — no box
          mki = locationAndMki.substring(4).trim();
        } else if (locationAndMki.includes("/")) {
          // split on FIRST slash only to preserve N/A inside mki value
          const slashIndex = locationAndMki.indexOf("/");
          box = locationAndMki.substring(0, slashIndex).trim();
          const mkiPart = locationAndMki.substring(slashIndex + 1).trim();
          if (mkiPart.toUpperCase().startsWith("MKB#")) {
            mki = mkiPart.substring(4).trim();
          } else {
            mki = mkiPart;
          }
        } else {
          box = locationAndMki;
        }
        key = remainingText;
      }

      return { box, mki, key, stock: stockSplit.stock };
    },
    buildAppItemNo(modelData, itemNum, mfgVal) {
      if (!modelData) modelData = {};
      let drawer = modelData.drawer || "";
      let slot = modelData.slot || "";
      let stock = modelData.stock !== undefined && modelData.stock !== null && modelData.stock !== "" ? modelData.stock : "";

      let str = "";
      if (itemNum) {
        str += `ITEM # ${itemNum} `;
      } else {
        str += `ITEM # `;
      }

      const getDefaultMfgVal = () => {
        const defaultOpt = this.mfgOptions.find((opt) => opt.name?.toLowerCase() === "default");
        const val = defaultOpt ? defaultOpt.value : "";
        return val.split("(")[1] || "";
      };

      if (mfgVal === "NONE" || mfgVal === "N/A" || !mfgVal) {
        str += `(${getDefaultMfgVal()} `;
      } else {
        const splitVal = mfgVal.split("(")[1];
        if (splitVal !== undefined) {
          str += `(${splitVal} `;
        } else {
          str += `(${getDefaultMfgVal()} `;
        }
      }

      if (drawer) {
        str += `DRAWER # (${drawer}) `;
      } else {
        str += `DRAWER # (?) `;
      }

      if (slot) {
        str += `SLOT (${slot}) `;
      } else {
        str += `SLOT (?) `;
      }

      if (stock !== "") {
        str += `*STOCK: (${stock}) `;
      } else {
        str += `*STOCK: (?) `;
      }

      return str;
    },
    handleSyncError(error, localEdits) {
      this.removeLocalEdits(localEdits);
      const message = (error && error.message) || String(error);
      this.syncErrorShown = true;
      this.syncError = getSyncErrorMessage(error);
      errorLogsWrapper({
        module: "MechanicalKey sync",
        description: "syncMechanicalKeyInsertBlade failed",
        errorDetails: {
          message,
          code: (error && error.code) || null,
          stack: (error && error.stack) || null,
        },
        context: {
          keyType: "mechanicalKey",
          applications: localEdits.map((e) => ({ makeId: e.makeId, modelId: e.modelId })),
        },
        createdBy: auth.currentUser?.email || "-",
      });
    },
    addLocalEdits(edits) {
      this.syncErrorShown = false;
      this.syncError = "";
      this.localEdits = [...this.localEdits, ...edits];
      // Safety net: never let a local edit hide server data for long
      setTimeout(() => this.removeLocalEdits(edits), 10000);
    },
    removeLocalEdits(edits) {
      this.localEdits = this.localEdits.filter((edit) => !edits.includes(edit));
    },
    async saveEdit() {
      if (!this.editItem) return;

      this.savingEdit = true;
      this.$root.loader = true;
      try {
        let manufacturer = (this.editForm.manufacturer || this.defaultMfg || "").toString().trim();
        let itemNumber = (this.editForm.itemNumber || "").toString().trim();
        const updates = [];
        this.editItem.applications.forEach((application) => {
          if (!application.makeId || !application.modelId) return;
          updates.push({
            makeId: application.makeId,
            modelId: application.modelId,
            key: {
              box: this.editForm.box,
              name: this.editForm.key,
              code: this.editForm.mki,
              stock: this.editForm.stock,
            },
            fields: {
              mfg: manufacturer,
              item_number: itemNumber,
              app_item_no: this.buildAppItemNo(application.modelData || {}, itemNumber, manufacturer),
            },
          });
        });

        // Show the change locally right away; dropped once the server data
        // arrives or if the sync fails
        const keyText = this.buildKeyText(
          this.editForm.box,
          this.editForm.key,
          this.editForm.mki,
          this.editForm.stock
        );
        const localEdits = updates.map((u) => ({
          makeId: u.makeId,
          modelId: u.modelId,
          fields: { ...u.fields, mechanical_key: keyText },
        }));
        this.addLocalEdits(localEdits);

        // Cloud function applies the sync server-side; not awaited
        functions.httpsCallable("syncMechanicalKeyInsertBlade")({
          keyType: "mechanicalKey",
          updates,
        }).catch((error) => this.handleSyncError(error, localEdits));

        // Keep the loader visible briefly
        await new Promise((r) => setTimeout(r, 700));
        this.closeEditDialog(true);
        this.tableKey += 1;
      } catch (error) {
        this.$root.loader = false;
        this.$root.alert(error.message);
      } finally {
        this.savingEdit = false;
        if (!this._isDestroyed) this.$root.loader = false;
      }
    },
    openIndividualEdit(app) {
      if (!app || !app.modelData) return;
      const parsedKey = this.parseKeyText(app.modelData.mechanical_key);
      this.individualEditItem = app;
      this.individualEditForm = {
        makeId: app.makeId,
        modelId: app.modelId,
        makeName: app.make,
        modelName: app.modelData.name || app.model || "",
        box: parsedKey.box,
        key: parsedKey.key,
        mki: parsedKey.mki,
        manufacturer: app.modelData.mfg || this.defaultMfg,
        itemNumber: app.modelData.item_number || "",
        stock: parsedKey.stock,
      };
      this.individualEditDialog = true;
    },
    closeIndividualEditDialog() {
      this.hoverApplication = null;
      this.individualEditDialog = false;
      this.individualEditItem = null;
      this.individualEditForm = {
        makeId: "",
        modelId: "",
        makeName: "",
        modelName: "",
        box: "",
        key: "",
        mki: "",
        manufacturer: "",
        itemNumber: "",
        stock: "",
      };
    },
    async saveIndividualEdit() {
      if (!this.individualEditItem) return;

      this.savingIndividualEdit = true;
      this.$root.loader = true;
      try {
        const makeId = this.individualEditForm.makeId;
        const modelId = this.individualEditForm.modelId;
        const newMakeName = this.individualEditForm.makeName.trim();
        const newModelName = this.individualEditForm.modelName.trim();
        const manufacturer = (this.individualEditForm.manufacturer || this.defaultMfg || "").toString().trim();
        const itemNumber = (this.individualEditForm.itemNumber || "").toString().trim();

        if (!newMakeName) throw new Error("Make name is required");

        const updatedModel = {
          ...(this.individualEditItem.modelData || {}),
          name: newModelName,
          mfg: manufacturer,
          item_number: itemNumber,
        };

        // Show the change locally right away; dropped once the server data
        // arrives or if the sync fails
        const localEdits = [
          {
            makeId,
            modelId,
            fields: {
              name: newModelName,
              mfg: manufacturer,
              item_number: itemNumber,
              app_item_no: this.buildAppItemNo(updatedModel, itemNumber, manufacturer),
              mechanical_key: this.buildKeyText(
                this.individualEditForm.box,
                this.individualEditForm.key,
                this.individualEditForm.mki,
                this.individualEditForm.stock
              ),
            },
          },
        ];
        this.addLocalEdits(localEdits);

        // Cloud function applies the sync server-side; not awaited
        functions.httpsCallable("syncMechanicalKeyInsertBlade")({
          keyType: "mechanicalKey",
          updates: [
            {
              makeId,
              modelId,
              key: {
                box: this.individualEditForm.box,
                name: this.individualEditForm.key,
                code: this.individualEditForm.mki,
                stock: this.individualEditForm.stock,
              },
              fields: {
                name: newModelName,
                mfg: manufacturer,
                item_number: itemNumber,
                app_item_no: this.buildAppItemNo(updatedModel, itemNumber, manufacturer),
              },
            },
          ],
          makeRename:
            newMakeName !== this.individualEditItem.make
              ? { makeId, name: newMakeName }
              : null,
        }).catch((error) => this.handleSyncError(error, localEdits));
        this.hoverApplication = null;

        // Keep the loader visible briefly
        await new Promise((r) => setTimeout(r, 700));
        this.closeIndividualEditDialog();
        this.tableKey += 1;
      } catch (error) {
        this.$root.loader = false;
        this.$root.alert(error.message);
      } finally {
        this.savingIndividualEdit = false;
        if (!this._isDestroyed) this.$root.loader = false;
      }
    },
    goToPrevPage() {
      this.currentPage -= 1;
      this.tableKey += 1;
    },
    goToNextPage() {
      this.currentPage += 1;
      this.tableKey += 1;
    },
    listenPendingModels() {
      if (this.unsubscribeTempModels) this.unsubscribeTempModels();

      this.unsubscribeTempModels = tempModels.onSnapshot((snap) => {
        this.pendingModels = snap.docs.map((doc) => {
          return {
            id: doc.id,
            ...doc.data(),
          };
        });
      });
    },
  },
  watch: {
    "$store.getters.makes"(makes) {
      // Drop local edits the server data has caught up with
      this.localEdits = this.localEdits.filter((edit) => {
        let make = (makes || []).find((make) => make.id === edit.makeId);
        let model = make && (make.models || []).find((m) => m.uId === edit.modelId);
        if (!model) return false;
        return !Object.keys(edit.fields).every((key) => model[key] === edit.fields[key]);
      });
    },
    currentItemsPerPage() {
      this.currentPage = 1;
      this.tableKey += 1;
    },
    processedList() {
      if (this.currentPage > this.totalPages) this.currentPage = 1;
      this.tableKey += 1;
    },
    searchChips(val) {
      if (!Array.isArray(val)) {
        this.searchChips = [];
        return;
      }

      const uniqueChips = this.normalizeSearchChips(val);
      const changed =
        uniqueChips.length !== val.length ||
        uniqueChips.some((item, index) => item !== val[index]);

      if (changed) {
        this.searchChips = uniqueChips;
      }
    },
  },
  mounted() {
    this.listenPendingModels();
  },
  beforeDestroy() {
    if (this.unsubscribeTempModels) {
      this.unsubscribeTempModels();
    }
  },
};
</script>

<style scoped>
/* Outer row hover */
.templates-table >>> tbody tr:hover {
  background: transparent !important;
}

/* Outer td */
.templates-table >>> tbody > tr > td {
  white-space: normal !important;
  word-break: break-word;
  min-width: 0;
  padding: 0 16px !important;
  border-right: 1px solid rgba(0, 0, 0, 0.5) !important;
  border-top: none !important;
  vertical-align: middle;
}

/* Header cells */
.templates-table >>> th {
  white-space: nowrap !important;
  min-width: 0;
  border-right: 1px solid rgba(0, 0, 0, 0.5) !important;
}

/* Thick group separator via box-shadow */
.templates-table >>> tbody > tr.group-first-row {
  box-shadow: 0 -3px 0 0 rgba(0, 0, 0, 0.45) !important;
}

.templates-table >>> tbody > tr.application-hover > td.application-hover-cell {
  background: rgba(25, 118, 210, 0.08) !important;
}

.tag-item {
  display: inline-flex;
  align-items: center;
  background: #1140a5;
  border-radius: 16px;
  padding: 4px 10px;
  margin: 2px;
  font-size: 13px;
}

.clear-btn {
  margin-left: 8px;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
}

.clear-btn:hover {
  color: red;
}

.chip-title {
  color: white;
}

.theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > td:not(.v-data-table__mobile-row), .theme--light.v-data-table > .v-data-table__wrapper > table > tbody > tr:not(:last-child) > th:not(.v-data-table__mobile-row) {
    border-bottom: thin solid rgba(0, 0, 0, 0.5);
}
</style>
